import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CLIP_MINUTES,
  DEFAULT_ASPECT,
  centralMinute,
  clipAt,
  coverageSegments,
  createBlobCache,
  fetchRelayBlobUrl,
  fetchRelayJson,
  fileUrl,
  fmtClock,
  fmtDateLong,
  fmtHour,
  listUrl,
  nextClip,
  sortClips,
  thumbUrl,
  todayCentral,
} from '../../utils/cameraRelay';
import { jobListRows, runAt } from '../../utils/stationActivity';
import DayBar, { RANGE_FULL, RANGE_WORKDAY, coverageToBlocks, runsToBlocks } from './DayBar';
import { C, Icon, PlayPauseIcon } from './CamerasStyles';
import { useStationRuns } from './useCameraData';
import { dayWindow, nightWatchLane } from '../../features/security/nightWatch';
import { useNightWatchTimeline } from '../../features/security/useNightWatch';

// The Night watch lane: armed stretches shaded, this camera's alerts as marks (grey = not texted).
const WATCH_LANE_COLORS = { armed: '#1c3557', offline: '#4a1d1d', fallback: '#3a2c12', snoozed: '#3a2c12',
                            mark: C.bad, markHeld: '#7d8693' };

const SPEEDS = [1, 2, 4, 8];
const STRIP_EVERY = 3; // one frame per 3 clips ≈ every 6 minutes

/** Where to start when nobody asked for a time: today → the newest clip, a past day → 7 AM. */
function defaultMinute(clips, date) {
  if (!clips.length) return null;
  if (date === todayCentral()) return clips[clips.length - 1].start;
  const first = clips.find((c) => c.start >= 420);
  return (first || clips[0]).start;
}

export default function PlaybackView({ camera, aspect, date, request, onPlayhead, onCopyLink }) {
  const videoRef = useRef(null);
  const blobCache = useRef(createBlobCache(4));
  const inflight = useRef(new Map());
  const thumbCache = useRef(createBlobCache(90));
  const pendingOffset = useRef(0);
  const wantPlay = useRef(false);
  const lastRequest = useRef(null);

  const [clips, setClips] = useState([]);
  const [listState, setListState] = useState('loading'); // loading | ready | error
  const [current, setCurrent] = useState(null); // { file, start }
  const [videoUrl, setVideoUrl] = useState('');
  const [clipLoading, setClipLoading] = useState(false);
  const [clipError, setClipError] = useState('');
  const [playhead, setPlayhead] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [range, setRangeState] = useState(null); // null = not chosen: 24 h on a day with alerts on this camera
  const setRange = (r) => setRangeState(r);
  const nightDay = useMemo(() => dayWindow(date), [date]);
  const { timeline: watchTimeline } = useNightWatchTimeline(true, nightDay.since, nightDay.until);
  const [stripHour, setStripHour] = useState(null);
  const [thumbs, setThumbs] = useState({});

  const station = camera.station;
  const index = camera.index;
  const isToday = date === todayCentral();
  const runsState = useStationRuns(camera.jobqStation ? [camera.jobqStation] : [], date);
  const runs = useMemo(
    () => (camera.jobqStation ? runsState[camera.jobqStation]?.runs || [] : []),
    [camera.jobqStation, runsState],
  );

  // ── Clip blobs (CF Access can't serve media ranges, so every clip is a blob) ──
  const getClipUrl = useCallback((file) => {
    const cached = blobCache.current.get(file);
    if (cached) return Promise.resolve(cached);
    if (inflight.current.has(file)) return inflight.current.get(file);
    const p = fetchRelayBlobUrl(fileUrl(station, index, file))
      .then((url) => { blobCache.current.set(file, url); return url; })
      .finally(() => inflight.current.delete(file));
    inflight.current.set(file, p);
    return p;
  }, [station, index]);

  const loadClip = useCallback(async (clip, offsetSec, play) => {
    setCurrent({ file: clip.file, start: clip.start });
    setPlayhead(clip.start + offsetSec / 60);
    setClipError('');
    pendingOffset.current = offsetSec;
    wantPlay.current = play;
    if (!blobCache.current.has(clip.file)) setClipLoading(true);
    try {
      const url = await getClipUrl(clip.file);
      setVideoUrl(url);
    } catch (e) {
      setClipError('This clip did not load.');
    } finally {
      setClipLoading(false);
    }
  }, [getClipUrl]);

  const seek = useCallback((minute, play = true) => {
    const hit = clipAt(clips, minute);
    if (!hit) return;
    setStripHour(Math.floor(hit.start / 60 + hit.offsetSec / 3600));
    const v = videoRef.current;
    if (current && hit.file === current.file && v && videoUrl) {
      v.currentTime = hit.offsetSec;
      setPlayhead(hit.start + hit.offsetSec / 60);
      if (play && v.paused) v.play().catch(() => {});
      return;
    }
    loadClip(hit, hit.offsetSec, play);
  }, [clips, current, videoUrl, loadClip]);

  // ── Load the day's clip list whenever the camera or date changes ──
  useEffect(() => {
    let cancelled = false;
    const cache = blobCache.current;
    const pending = inflight.current;
    setListState('loading');
    setClips([]);
    setCurrent(null);
    setVideoUrl('');
    setPlaying(false);
    fetchRelayJson(listUrl(station, index, date))
      .then((data) => {
        if (cancelled) return;
        setClips(sortClips(data?.files));
        setListState('ready');
      })
      .catch(() => { if (!cancelled) setListState('error'); });
    return () => {
      cancelled = true;
      cache.clear();
      pending.clear();
    };
  }, [station, index, date]);

  // Free every blob when the view goes away.
  useEffect(() => {
    const thumbsRef = thumbCache.current;
    return () => thumbsRef.clear();
  }, []);

  // ── Jump requests (a link, Rewind 5 min, a camera switch keeping the time) ──
  useEffect(() => {
    if (listState !== 'ready') return;
    const key = `${station}|${index}|${date}|${request?.nonce ?? 'none'}`;
    if (lastRequest.current === key) return;
    lastRequest.current = key;
    const minute = request?.minute ?? defaultMinute(clips, date);
    if (minute == null) return;
    seek(minute, Boolean(request?.play));
  }, [listState, clips, request, station, index, date, seek]);

  useEffect(() => { if (playhead != null) onPlayhead?.(playhead); }, [playhead, onPlayhead]);

  // ── Video element events ──
  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = speed;
    v.muted = muted;
    if (pendingOffset.current) {
      try { v.currentTime = Math.min(pendingOffset.current, Math.max(0, (v.duration || 0) - 0.1)); } catch (e) { /* noop */ }
    }
    if (wantPlay.current) {
      v.play().catch(() => {
        // Autoplay with sound refused: try again muted.
        v.muted = true;
        setMuted(true);
        v.play().catch(() => setPlaying(false));
      });
    }
    // Fetch the next clip now so it starts without a pause.
    const nxt = current ? nextClip(clips, current.file) : null;
    if (nxt) getClipUrl(nxt.file).catch(() => {});
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !current) return;
    setPlayhead(current.start + v.currentTime / 60);
  };

  const onEnded = async () => {
    if (!current) return;
    let list = clips;
    let nxt = nextClip(list, current.file);
    if (!nxt && isToday) {
      // Today keeps recording: re-read the list once before stopping.
      try {
        const data = await fetchRelayJson(listUrl(station, index, date));
        list = sortClips(data?.files);
        setClips(list);
        nxt = nextClip(list, current.file);
      } catch (e) { /* keep what we have */ }
    }
    if (nxt) loadClip(nxt, 0, true);
    else setPlaying(false);
  };

  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = speed; }, [speed]);
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || !videoUrl) {
      if (playhead != null) seek(playhead, true);
      return;
    }
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };
  const nudge = (sec) => { if (playhead != null) seek(Math.max(0, playhead + sec / 60), !videoRef.current?.paused); };

  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(e.shiftKey ? -60 : -10); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); nudge(e.shiftKey ? 60 : 10); }
  };

  const fullScreen = () => {
    const v = videoRef.current;
    if (v && v.requestFullscreen) v.requestFullscreen().catch(() => {});
  };

  // ── Filmstrip: the hour under the playhead, one frame per ~6 min ──
  const hour = stripHour ?? (playhead != null ? Math.floor(playhead / 60) : null);
  const strip = useMemo(() => {
    if (hour == null) return [];
    const inHour = clips.filter((c) => c.start >= hour * 60 && c.start < hour * 60 + 60);
    return inHour.filter((_, i) => i % STRIP_EVERY === 0);
  }, [clips, hour]);

  useEffect(() => {
    let cancelled = false;
    strip.forEach((c) => {
      const key = `${station}|${index}|${c.file}`;
      const hit = thumbCache.current.get(key);
      if (hit) {
        setThumbs((prev) => (prev[key] ? prev : { ...prev, [key]: hit }));
        return;
      }
      fetchRelayBlobUrl(thumbUrl(station, index, c.file))
        .then((url) => {
          thumbCache.current.set(key, url);
          if (!cancelled) setThumbs((prev) => ({ ...prev, [key]: url }));
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [strip, station, index]);

  const activeStrip = useMemo(() => {
    if (playhead == null || !strip.length) return null;
    let best = null;
    for (const c of strip) if (c.start <= playhead + 0.01) best = c;
    return best && playhead - best.start < STRIP_EVERY * CLIP_MINUTES + 0.5 ? best.file : null;
  }, [strip, playhead]);

  const segments = useMemo(() => coverageSegments(clips), [clips]);
  const watchBlocks = useMemo(() => nightWatchLane(watchTimeline, date, camera.key, WATCH_LANE_COLORS), [watchTimeline, date, camera.key]);
  const cameraAlerted = (watchTimeline?.incidents || []).some((a) => a.camera_key === camera.key);
  const activeRange = range === 'full' || (range == null && cameraAlerted)
    || (playhead != null && (playhead < RANGE_WORKDAY[0] || playhead > RANGE_WORKDAY[1]))
    ? RANGE_FULL : RANGE_WORKDAY;
  const lanes = [{ key: 'rec', label: 'Recorded', height: 10, blocks: coverageToBlocks(segments) }];
  if (camera.jobqStation) lanes.push({ key: 'jobq', label: `JobQ · ${camera.jobqStation}`, height: 26, blocks: runsToBlocks(runs) });
  if (watchBlocks.length) lanes.push({ key: 'watch', label: 'Night watch', height: 14, minLabelPct: 6, blocks: watchBlocks });
  const listRows = useMemo(() => jobListRows(runs), [runs]);
  const activeRun = playhead != null ? runAt(listRows, playhead) : null;
  const jobRuns = listRows.filter((r) => r.state === 'Working');

  const saveClip = () => {
    if (!videoUrl || !current) return;
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `${camera.slug}_${current.file}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const frameAspect = aspect || camera.aspect || DEFAULT_ASPECT;

  return (
    <main style={{ display: 'flex', flexWrap: 'wrap', gap: 18, padding: '14px 24px 28px', alignItems: 'flex-start' }}>
      <section style={{ flex: '999 1 720px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          className="jqc-frame"
          tabIndex={0}
          onKeyDown={onKeyDown}
          aria-label={`${camera.label} recording. Space plays or pauses, arrow keys skip 10 seconds.`}
          style={{ aspectRatio: String(frameAspect), borderRadius: 12, border: `1px solid ${C.border}` }}
        >
          {videoUrl && (
            <video
              ref={videoRef}
              key={videoUrl}
              src={videoUrl}
              className="jqc-contain"
              playsInline
              onClick={togglePlay}
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={onEnded}
            />
          )}
          {(listState === 'loading' || clipLoading) && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, background: videoUrl ? 'rgba(0,0,0,.35)' : 'transparent' }}>
              {listState === 'loading' ? 'Loading recordings…' : 'Loading clip…'}
            </div>
          )}
          {listState === 'ready' && clips.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, padding: 24, textAlign: 'center' }}>
              No recordings from {camera.label} on {fmtDateLong(date)}.
            </div>
          )}
          {listState === 'error' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.warn }}>
              Couldn't load the recordings list.
            </div>
          )}
          {clipError && !clipLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', justifyContent: 'center', color: C.warn }}>
              {clipError}
              <button type="button" className="jqc-btn" onClick={() => playhead != null && seek(playhead, true)}>Try again</button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <button type="button" className="jqc-btn jqc-icon" aria-label={playing ? 'Pause' : 'Play'} onClick={togglePlay} disabled={!clips.length}>
            <PlayPauseIcon playing={playing} />
          </button>
          <button type="button" className="jqc-btn jqc-icon" aria-label="Back 10 seconds" onClick={() => nudge(-10)} disabled={!clips.length}><Icon name="rewind" size={17} /></button>
          <button type="button" className="jqc-btn jqc-icon" aria-label="Forward 10 seconds" onClick={() => nudge(10)} disabled={!clips.length}><Icon name="forward" size={17} /></button>
          <span style={{ fontSize: 16, fontWeight: 500, margin: '0 6px' }}>{playhead != null ? fmtClock(playhead, { seconds: true }) : '—'}</span>
          {activeRun && activeRun.job != null && (
            <span className="jqc-ell" style={{ color: C.muted, fontSize: 12, maxWidth: 320 }}>Job {activeRun.job}{activeRun.user ? ` · ${activeRun.user}` : ''}</span>
          )}
          <span style={{ flex: 1 }} />
          <div className="jqc-seg" role="group" aria-label="Playback speed">
            {SPEEDS.map((s) => (
              <button key={s} type="button" aria-pressed={speed === s} onClick={() => setSpeed(s)}>{s}×</button>
            ))}
          </div>
          <button type="button" className="jqc-btn" aria-pressed={muted} onClick={() => setMuted((m) => !m)}>{muted ? 'Sound off' : 'Sound on'}</button>
          <button type="button" className="jqc-btn" onClick={() => playhead != null && onCopyLink(playhead)} disabled={playhead == null}><Icon name="link" />Copy link</button>
          <button type="button" className="jqc-btn" onClick={saveClip} disabled={!videoUrl}><Icon name="download" />Save clip</button>
          <button type="button" className="jqc-btn jqc-icon" aria-label="Full screen" onClick={fullScreen} disabled={!videoUrl}><Icon name="expand" /></button>
        </div>

        <div className="jqc-card" style={{ padding: '14px 16px 10px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{fmtDateLong(date)}</h2>
            <span style={{ color: C.faint, fontSize: 12 }}>click or drag anywhere on the day to jump there</span>
            <span style={{ flex: 1 }} />
            <div className="jqc-seg" role="group" aria-label="Time range">
              <button type="button" aria-pressed={activeRange === RANGE_WORKDAY} onClick={() => setRange('workday')}>6a – 10p</button>
              <button type="button" aria-pressed={activeRange === RANGE_FULL} onClick={() => setRange('full')}>24 h</button>
            </div>
          </div>
          <DayBar
            lanes={lanes}
            range={activeRange}
            playhead={playhead}
            now={isToday ? centralMinute() : null}
            highlight={strip.length ? [hour * 60, hour * 60 + 60] : null}
            onSeek={(m) => seek(m, true)}
            ariaLabel={`${camera.label} on ${fmtDateLong(date)}`}
          />
        </div>

        {hour != null && (
          <div className="jqc-card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', marginBottom: 10 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{fmtHour(hour)} – {fmtHour(hour + 1)}</h2>
              <span style={{ color: C.faint, fontSize: 12 }}>a frame every 6 minutes · click one to jump</span>
              <span style={{ flex: 1 }} />
              <button type="button" className="jqc-btn" onClick={() => setStripHour(Math.max(0, hour - 1))} disabled={hour <= 0}>Earlier hour</button>
              <button type="button" className="jqc-btn" onClick={() => setStripHour(Math.min(23, hour + 1))} disabled={hour >= 23}>Later hour</button>
            </div>
            {strip.length === 0 ? (
              <div style={{ color: C.faint, fontSize: 13, padding: '8px 0' }}>Nothing recorded in this hour.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, minmax(0, 1fr))', gap: 6, minWidth: 760 }}>
                  {strip.map((c) => {
                    const url = thumbs[`${station}|${index}|${c.file}`];
                    return (
                      <button key={c.file} type="button" className="jqc-thumb" aria-current={activeStrip === c.file} onClick={() => seek(c.start, true)} aria-label={`Jump to ${fmtClock(c.start)}`}>
                        <span className="jqc-frame jqc-r-std" style={{ borderRadius: 6 }}>
                          {url && <img src={url} alt="" />}
                        </span>
                        <span style={{ textAlign: 'center' }}>{fmtClock(c.start).replace(/ [AP]M$/, '')}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <aside style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {camera.jobqStation ? (
          <div className="jqc-card" style={{ padding: '12px 8px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '0 8px 8px' }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{camera.jobqStation} in JobQ</h2>
              <span style={{ color: C.faint, fontSize: 12 }}>
                {runsState[camera.jobqStation]?.loading ? 'loading…' : runsState[camera.jobqStation]?.error ? "couldn't load" : `${jobRuns.length} job${jobRuns.length === 1 ? '' : 's'}`}
              </span>
            </div>
            {!runsState[camera.jobqStation]?.loading && listRows.length === 0 && (
              <div style={{ color: C.faint, fontSize: 13, padding: '4px 10px 8px' }}>No JobQ work logged at {camera.jobqStation} this day.</div>
            )}
            <div style={{ maxHeight: 760, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {listRows.map((r) => {
                const maint = r.state !== 'Working';
                const on = activeRun === r;
                return (
                  <button
                    key={`${r.start}-${r.job}`}
                    type="button"
                    className="jqc-row"
                    aria-current={on}
                    aria-label={`Jump to ${fmtClock(r.start)}, ${maint ? 'maintenance' : `job ${r.job}`}`}
                    onClick={() => seek(r.start, true)}
                    style={maint ? { color: C.faint } : undefined}
                  >
                    <span style={{ width: 90, flex: 'none', fontSize: 12, color: C.muted, paddingTop: 1 }}>
                      {fmtClock(r.start).replace(/ [AP]M$/, '')}–{fmtClock(r.end).replace(/ [AP]M$/, '')}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                      <span className="jqc-ell" style={{ fontSize: 13 }}>
                        <span style={{ fontWeight: 600 }}>{maint ? 'Maintenance' : `Job ${r.job}`}</span>{!maint && r.desc ? ` ${r.desc}` : ''}
                      </span>
                      {!maint && (r.user || r.stops > 0) && (
                        <span style={{ fontSize: 12, color: C.faint }}>
                          {[r.user, r.stops > 0 ? `${r.stops} maintenance stop${r.stops === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap', paddingTop: 1 }}>{Math.max(1, Math.round(r.end - r.start))} min</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="jqc-card" style={{ padding: '14px 16px' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>{camera.label}</h2>
            <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Not tied to a JobQ station, so there are no jobs to jump to. Use the day bar and the filmstrip.</p>
          </div>
        )}
      </aside>
    </main>
  );
}

